// .ts extension: these modules are in the node --test import graph, which resolves literally
import { i18n } from '../i18n/index.ts';
import { boardSourceQuery, pageParams, serverFilter } from './paging.ts';
import type { ServerQuery } from './paging.ts';
import { dedupeKey } from './payload.ts';
import type { QuestionBankItem, QuestionBankSource } from './types.ts';

/**
 * The device-local bank, and what happens to it now the questions live on the server.
 *
 * Teachers on the current build have questions under this key and nowhere else. It is read for
 * the one-time upload offer and never written or cleared again: it is their only copy until the
 * upload lands, and these panels are shared school devices where the account that opens
 * /kho-bai-tap first is not necessarily the account that typed the questions. So the upload is
 * the teacher's decision, not ours - see importLegacy in store.ts.
 */
export const QUESTION_BANK_STORAGE_KEY = 'msb.question-bank.v1';

/** Set once the teacher has uploaded or dismissed the local bank, so the offer is made once. */
export const QUESTION_BANK_MIGRATED_KEY = 'msb.question-bank.migrated.v1';

function isLegacyItem(value: unknown): value is QuestionBankItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return typeof item.content === 'string' && item.content !== '' && typeof item.type === 'string';
}

/** zustand persist wrote `{ state: { items, filter }, version }` - anything else is not our bank. */
export function parseLegacyItems(raw: string | null): QuestionBankItem[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return [];
  }
  const state = parsed && typeof parsed === 'object' ? (parsed as { state?: unknown }).state : null;
  const items = state && typeof state === 'object' ? (state as { items?: unknown }).items : null;
  if (!Array.isArray(items)) return [];
  return items.filter(isLegacyItem);
}

/**
 * Local questions the account does not already hold.
 *
 * Content is the only key available - the server stores no client id - so this is exact-match
 * dedupe on type, level and the sanitized HTML. It makes a second upload a no-op, which is what
 * it is for; it does NOT survive a question being edited on another device, and that case
 * duplicates. The teacher sees the count before tapping and can delete, which is why an
 * imperfect key is acceptable here and would not be for an automatic upload.
 */
export function pickUnsynced(local: QuestionBankItem[], remote: QuestionBankItem[]): QuestionBankItem[] {
  const seen = new Set<string>();
  remote.forEach((item) => seen.add(dedupeKey(item)));
  const picked: QuestionBankItem[] = [];
  local.forEach((item) => {
    const key = dedupeKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    picked.push(item);
  });
  return picked;
}

/** Rows per request, and the most pages one source may take before the read gives up. */
export const BOARD_MADE_PAGE_SIZE = 200;
export const BOARD_MADE_MAX_PAGES = 50;

/** Every board source; a question made anywhere else carries none of them. */
export const BOARD_SOURCES: QuestionBankSource[] = ['manual', 'ai-quiz-supporter'];

export type BoardMadeLookup = (query: ServerQuery) => Promise<{ items: QuestionBankItem[]; fetched: number }>;

/**
 * Every board-made question in the school bank, one page-through per source.
 *
 * The local bank can only have reached IZ through a board, and every board upload is stamped
 * with config.msb.source, so these rows are the whole dedupe set: no html_contain scan, and no
 * createdBy, because the local bank has no owner and another teacher may have uploaded it.
 * A failed page, or a source past the page cap, throws: the caller hides the offer rather than
 * over-count.
 */
export async function fetchBoardMade(
  lookup: BoardMadeLookup,
  size: number = BOARD_MADE_PAGE_SIZE,
  maxPages: number = BOARD_MADE_MAX_PAGES,
): Promise<QuestionBankItem[]> {
  let all: QuestionBankItem[] = [];
  for (let s = 0; s < BOARD_SOURCES.length; s += 1) {
    const filter = { ...serverFilter({}, null), ...boardSourceQuery(BOARD_SOURCES[s]) };
    let done = false;
    for (let page = 1; page <= maxPages && !done; page += 1) {
      const batch = await lookup({ ...filter, ...pageParams(page, size) });
      all = all.concat(batch.items);
      // `fetched` counts rows of hidden types too, so a page of only those still pages on
      done = batch.fetched < size;
    }
    if (!done) throw new Error(i18n.t('questionBank:messages.legacyTooMany', { count: maxPages * size }));
  }
  return all;
}

export function readLegacyItems(): QuestionBankItem[] {
  try {
    return parseLegacyItems(localStorage.getItem(QUESTION_BANK_STORAGE_KEY));
  } catch (error) {
    return [];
  }
}

/** A storage that cannot be read is treated as migrated: better a missed offer than a loop. */
export function isLegacyMigrated(): boolean {
  try {
    return localStorage.getItem(QUESTION_BANK_MIGRATED_KEY) === '1';
  } catch (error) {
    return true;
  }
}

// Only the flag is written. QUESTION_BANK_STORAGE_KEY is left exactly as it was, on purpose.
export function markLegacyMigrated(): void {
  try {
    localStorage.setItem(QUESTION_BANK_MIGRATED_KEY, '1');
  } catch (error) {
    // a full disk only means the offer comes back next time, which is harmless
  }
}
